/**
 * @file numberFormatters.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-01
 * @modified 2026-10-08
 * @description Shared numeric display helpers. Use these instead of bare
 *              `.toFixed(2)` so every quantity in the app renders the same.
 *
 * THE QUANTITY RULE (Cam, restated 2026-10-08: "Quantities throughout the
 * system should be to 2 dp unless the qty value has more, then show all of them
 * up to and not including the last 0"):
 *
 *   2       → "2.00"       1.2    → "1.20"      1.025 → "1.025"
 *   1.0300  → "1.03"       0.0375 → "0.0375"    1250  → "1,250.00"
 *
 * Never fewer than 2 decimal places, as many more — up to 4 — as the figure
 * has, never a trailing zero past the 2nd. Every quantity column is
 * numeric(12,4) and Xero's ceiling is 4dp; a COMPUTED figure (tonnes = packs ×
 * kg / 1000) is rounded half-up at the 4th place (roundQuantity).
 *
 * THIS FILE IS THE ONE SOURCE. The server runs a CommonJS copy of it
 * (utils/numberFormatters.js, re-exported by utils/numberFormat.js) written by
 * `node scripts/sync-shared-utils.js` — edit here, then re-run that; the
 * server's sharedCopies test fails while the copy is stale. Keep it free of
 * imports and `export default` so the copy can be generated.
 * Tonnes always carry a capital T (formatTonnes).
 *
 * THE ONE EXCEPTION — COUNTS (Cam, 2026-10-08: "Just show to however many dp
 * are needed (usually no dp)"): a number of packs, bags, totes or pallets is a
 * count, not a quantity, and shows only the places it has — 4 → "4", 40 → "40",
 * 1.5 → "1.5", 2.25 → "2.25", 1.33333 → "1.3333" (half-up at the 4th, as
 * above). formatCount. Tonnes, kg and a line's quantity keep the 2dp minimum.
 */

const MAX_QTY_DP = 4;

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Round half-up (away from zero) at `dp` places, without float noise: cut to
 * 15 significant digits first, then shift by the exponent rather than
 * multiplying (1.005 * 100 is 100.49999…). null for blank / non-numeric.
 */
export function roundHalfUp(value, dp = MAX_QTY_DP) {
  const n = toNumber(value);
  if (n === null) return null;
  const abs = Number(Math.abs(n).toPrecision(15));
  let r = Number(`${Math.round(Number(`${abs}e${dp}`))}e-${dp}`);
  if (!Number.isFinite(r)) r = Math.round(abs * 10 ** dp) / 10 ** dp; // "1e-7e4" and the like
  if (r === 0) return 0; // never "-0.00"
  return n < 0 ? -r : r;
}

/** A quantity rounded to the 4 places it may be shown at; null when blank. */
export const roundQuantity = (value) => roundHalfUp(value, MAX_QTY_DP);

const render = (value, grouping, minDp = 2) => {
  const r = roundQuantity(value);
  if (r === null) return '';
  return r.toLocaleString('en-GB', {
    minimumFractionDigits: minDp,
    maximumFractionDigits: MAX_QTY_DP,
    useGrouping: grouping,
  });
};

/**
 * Format a quantity for display — "1,250.00", "29.46", "29.4567".
 *
 * Returns '' for null/undefined/empty/non-finite input — callers can
 * substitute their own placeholder ("-", "—", etc.) without a guard.
 */
export function formatQuantity(value) {
  return render(value, true);
}

/**
 * A COUNT — packs, bags, totes, pallets: only the decimal places the figure
 * has, up to 4, half-up — "4", "40", "1.5", "2.25", "1,250", "1.3333". The one
 * exception to the 2dp minimum (Cam, 2026-10-08). '' when blank.
 */
export function formatCount(value) {
  return render(value, true, 0);
}

/** A count for the VALUE of an input: "1250", "1.5" — no separators. */
export function formatCountInput(value) {
  return render(value, false, 0);
}

/** A tonnage with its capital T: "12.50 T", "0.0375 T" — '' when blank. */
export function formatTonnes(value) {
  const text = formatQuantity(value);
  return text ? `${text} T` : '';
}

/**
 * The same rule, for the VALUE of an input rather than a label: no thousands
 * separators (a number input cannot take "1,250.00", and parseFloat would read
 * it as 1), and no currency symbol. Use it when a stored figure is loaded into
 * a field or when a field is left (blur) — never while someone is typing, or it
 * will rewrite "12." into "12.00" under their cursor.
 *
 * Returns '' for blank or non-numeric input, so an empty field stays empty.
 */
export function formatDecimalInput(value) {
  return render(value, false);
}
